import type { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';
import { cn } from '@/shared/lib/cn';
import { ConfigurationField } from './ConfigurationField';
import { MinimumStayTierControls } from './MinimumStayTierControls';
import type { PricingConfigurationFormValues } from './pricingConfigurationSchema';

const STAY_FIELDS = [
  ['regularMinimumStay', 'Regulär'],
  ['fridaySaturdayMinimumStay', 'Freitag und Samstag'],
  ['maximumGapLength', 'Längste Lücke'],
] as const;

const GAP_POLICIES = [
  ['Always', 'immer'],
  ['WithinDays', 'nur kurzfristig'],
] as const;

interface Props {
  form: UseFormReturn<PricingConfigurationFormValues>;
  tiers: UseFieldArrayReturn<PricingConfigurationFormValues, 'minimumStayLeadTimeTiers'>;
  errorFor: (path: string) => string | undefined;
}

/** Abschnitt 3: Mindestnächte, Lückenregel und Vorlaufstaffel (Board „Preisautomatik · Einstellungen“). */
export function MinimumStaySection({ form, tiers, errorFor }: Props) {
  const gapPolicy = form.watch('gapPolicy');
  return (
    <>
      <div className="grid gap-2.5 sm:grid-cols-3">
        {STAY_FIELDS.map(([name, label]) => (
          <ConfigurationField
            key={name}
            label={label}
            type="number"
            inputMode="numeric"
            min="1"
            max="30"
            suffix="Nächte"
            error={errorFor(name)}
            {...form.register(name)}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2.5">
        <span id="gap-policy-label" className="text-sm font-bold">
          Lücken füllen
        </span>
        <div
          role="radiogroup"
          aria-labelledby="gap-policy-label"
          className="rounded-pill bg-card-alt flex gap-0.5 p-1"
        >
          {GAP_POLICIES.map(([value, label]) => (
            <label
              key={value}
              className={cn(
                // Sichtbar 36 px; die Trefferfläche reicht über das Polster der Gruppe auf 44 px (§9).
                "rounded-pill has-[:focus-visible]:outline-forest relative flex h-9 cursor-pointer items-center px-3.5 text-[13px] before:absolute before:-inset-y-1 before:inset-x-0 before:content-[''] has-[:focus-visible]:outline has-[:focus-visible]:outline-2",
                gapPolicy === value ? 'bg-card font-bold shadow-sm' : 'text-ink-soft font-semibold',
              )}
            >
              <input
                type="radio"
                value={value}
                className="sr-only"
                {...form.register('gapPolicy')}
              />
              {label}
            </label>
          ))}
        </div>
        {gapPolicy === 'WithinDays' && (
          <ConfigurationField
            label="Nur innerhalb von"
            type="number"
            inputMode="numeric"
            min="1"
            max="500"
            suffix="Tagen"
            className="w-44"
            error={errorFor('gapWithinDays')}
            {...form.register('gapWithinDays')}
          />
        )}
      </div>
      <MinimumStayTierControls form={form} tiers={tiers} errorFor={errorFor} />
    </>
  );
}
